// ══════════════════════════════════════════════════════════════════════════════
// Verdict display helpers
// Convert internal verdict labels into unambiguous user-facing text.
//   Internal "Pass"   = reject the deal  → "PASS ON DEAL" (or "NO-GO")
//   Internal "Proceed"= approve           → "GO"
//   Internal "Caution"= conditional       → "PROCEED WITH CAUTION"
// Never display only "Pass" — it can be misread as "passed underwriting".
// ══════════════════════════════════════════════════════════════════════════════

export type VerdictKind = "GO" | "MAYBE" | "NO-GO";

export interface VerdictDisplay {
  /** Long form for hero headlines and decision panels. */
  long: string;
  /** Short form for chips, table cells, sensitivity rows. */
  short: string;
  /** Tone for color theming. */
  tone: "positive" | "warning" | "negative";
}

/**
 * Map any verdict input (engine label, scenario verdictShift, raw verdict
 * enum) to an unambiguous display string.
 */
export function getVerdictDisplay(
  input: string | VerdictKind | null | undefined,
): VerdictDisplay {
  const v = String(input ?? "").trim().toLowerCase();

  // Reject (NO-GO) — also covers raw "no", "no-go", "fail", "reject"
  if (
    v === "pass" ||
    v === "no" ||
    v === "no-go" ||
    v === "nogo" ||
    v === "reject" ||
    v === "fail" ||
    v === "pass on deal"
  ) {
    return { long: "PASS ON DEAL", short: "PASS ON DEAL", tone: "negative" };
  }

  // Approve (GO)
  if (v === "proceed" || v === "go" || v === "approve") {
    return { long: "GO", short: "GO", tone: "positive" };
  }

  // Conditional (MAYBE / Caution)
  if (
    v === "caution" ||
    v === "maybe" ||
    v === "proceed with caution" ||
    v === "conditional"
  ) {
    return {
      long: "PROCEED WITH CAUTION",
      short: "PROCEED WITH CAUTION",
      tone: "warning",
    };
  }

  // Fallback — uppercase and pass through
  const upper = String(input ?? "").toUpperCase();
  return { long: upper, short: upper, tone: "warning" };
}

/** Convenience: short label only. */
export function verdictShort(input: string | VerdictKind | null | undefined): string {
  return getVerdictDisplay(input).short;
}

/** Convenience: long label only. */
export function verdictLong(input: string | VerdictKind | null | undefined): string {
  return getVerdictDisplay(input).long;
}

// ══════════════════════════════════════════════════════════════════════════════
// Canonical decision resolver
// Single source of truth for verdict/confidence/risk across the app.
// Precedence: orchestrator.final_decision > scoring engine fallback.
// All user-facing surfaces (Detail, Memo, Copilot, Export, Negotiation) MUST
// resolve through this helper so labels never drift.
// ══════════════════════════════════════════════════════════════════════════════

export interface CanonicalDecision {
  verdict: VerdictDisplay;
  /** Raw verdict kind in canonical short form. */
  kind: "GO" | "CAUTION" | "PASS ON DEAL" | "UNKNOWN";
  /** Confidence score 0–100 if available. */
  confidence: number | null;
  /** Top risks surfaced for narrative/UI use. */
  risks: string[];
  /** Which layer resolved the decision. */
  source: "orchestrator" | "scoring" | "fallback";
}

interface DecisionInput {
  /** Orchestrator output (preferred). */
  orchestrator?: {
    final_decision?: string | null;
    confidence_score?: number | null;
    top_risks?: string[] | null;
  } | null;
  /** Scoring engine output (fallback). */
  scoring?: {
    verdict?: string | null;
    confidence_score?: number | null;
    risks?: string[] | null;
  } | null;
}

/**
 * Resolve the canonical decision for a deal. Always prefer the orchestrator's
 * final_decision; fall back to the scoring engine when missing.
 */
export function getCanonicalDecision(input: DecisionInput): CanonicalDecision {
  const orch = input.orchestrator ?? null;
  const sc = input.scoring ?? null;

  const rawVerdict =
    (orch?.final_decision && String(orch.final_decision).trim()) ||
    (sc?.verdict && String(sc.verdict).trim()) ||
    null;

  const verdict = getVerdictDisplay(rawVerdict);
  const kind: CanonicalDecision["kind"] =
    verdict.short === "GO" ? "GO" :
    verdict.short === "PROCEED WITH CAUTION" ? "CAUTION" :
    verdict.short === "PASS ON DEAL" ? "PASS ON DEAL" :
    "UNKNOWN";

  const confidence =
    typeof orch?.confidence_score === "number" ? orch.confidence_score :
    typeof sc?.confidence_score === "number" ? sc.confidence_score :
    null;

  const risks = (orch?.top_risks?.length ? orch.top_risks : sc?.risks) ?? [];

  const source: CanonicalDecision["source"] =
    orch?.final_decision ? "orchestrator" :
    sc?.verdict ? "scoring" :
    "fallback";

  return { verdict, kind, confidence, risks: [...risks], source };
}
